import React, { useState } from 'react';
import { Button, Modal, Form, Input, InputNumber, message, Spin } from 'antd';
import { useMutation } from '@apollo/client';
import { flags } from '../../featureFlags/flags';
import { PlusOutlined } from '@ant-design/icons';
import { CREATE_CERTIFICATE_TEMPLATE_OVERRIDE } from '../../gql'; 
import { authService } from '../../auth/authService';
import RosterGroups from '../../updated/components/RosterGroups/RosterGroups';
import FileUpload from '../../components/formItems/FileUpload/FileUpload';

export const CreateOverride = ( { certificateId, addCertificateCallback } ) => {
    const [ formController ] = Form.useForm();
    const [ isVisible, setIsVisible ] = useState( false );
    const [ isSaving, setIsSaving ] = useState( false );
    const [ groupSegmantationPayload ] = useState( 
        ( flags.SEGMENTATION_CLIENT_MANAGED_ATTRIBUTES_ENABLED === true || 
        flags.SEGMENTATION_CLIENT_MANAGED_ATTRIBUTES_ENABLED === 'true' )
    );
    const [ rosterEditEnabled ] = useState( flags.ROSTER_EDIT_ENABLED );

    const initialFormValues = {
        name: '',
        description: '',
        weight: 0,
        segments: [],
        certificateTemplateId: certificateId
    };
    
    const [ createOverride ] = 
        useMutation( CREATE_CERTIFICATE_TEMPLATE_OVERRIDE );

    /*************************************
     *  ANCHOR Event Handlers
     *************************************/
 
    const handleFormSubmission = ( values ) => {
        setIsSaving( true );
        
        createOverride( {
            variables: {
                input: { ...values, 
                    segments: values.segments.join( ',' )
                }
            },
        } ).then( res => {
            addCertificateCallback( res.data.result );
            message.success( 'Certificate Saved' );
            setIsVisible( false );
            setIsSaving( false );
        } ).catch( err => {
            message.error( 'Error saving certificate' );
            setIsSaving( false );
        } );
    };

    const resetForm = () => {
        formController.setFields( [ 
            { 
                name: 'certificateTemplateId', 
                value: certificateId
            },
            { 
                name: 'name', 
                value: ''
            },
            { 
                name: 'description', 
                value: ''
            },
            { 
                name: 'weight', 
                value: 0
            }, 
            { 
                name: 'segments', 
                value: []
            }, 
            { 
                name: 'file', 
                value: undefined 
            }
        ] );
    };

    const handleSelectFile = ( file ) => {
        if ( !file ) {
            formController.setFields( [ { name: 'file', value: undefined} ] );  
        } else {
            formController.setFields( [ { name: 'file', value: file} ] );
        }
    };

    const handleCancel = () => {
        resetForm();
        setIsVisible( false );
    };

    const handleShowModal = () => { 
        resetForm();
        setIsVisible( true );
    };

    const handleSetSegments = ( segments ) => { 
        formController.setFields( [ 
            { 
                name: 'segments', 
                value: segments
            }
        ] );
    };

    return (
        <>
            <Button
                type="primary"
                icon={ <PlusOutlined />}
                style={ { marginBottom: '20px', float: 'right' } }
                onClick={ () => {
                    handleShowModal();
                } }
            >
                Add Override
            </Button>

            { isVisible &&
                <Modal
                    open={ isVisible }
                    title="Create Override"
                    onCancel={ handleCancel }
                    footer={ [
                        isSaving ? <Spin style={ { marginRight: '10px' } } /> : null,
                        <Button 
                            key="1"
                            disabled={ isSaving }
                            onClick={ handleCancel }
                        >
                            Cancel
                        </Button>,
                        <Button
                            key="2"
                            type="primary"
                            disabled={ isSaving }
                            onClick={ () => formController.submit() }
                        >
                            Save
                        </Button>,
                    ] }
                >
                    <Form
                        form={ formController }
                        name="notification-form"
                        layout="vertical"
                        initialValues={ initialFormValues }
                        onFinish={ handleFormSubmission }
                        disabled={ isSaving }
                        scrollToFirstError
                    >   
                        { /* Name */ }
                        <Form.Item
                            name="name"
                            label="Name"
                            maxLength= { 64 }
                            rules={ [
                                {
                                    required: true,
                                    message: 'This is required to have a name.',
                                },
                                {
                                    max: 64,
                                    type: 'string',
                                    message: 'The name must be 64 characters or shorter.',
                                }
                            ] }
                        >
                            <Input />
                        </Form.Item>
                        
                        { /* Weight */ }
                        <Form.Item
                            name="weight"
                            label="Weight"       
                            rules={ [
                                {
                                    required: true,
                                    message: 'Weight is required to have a number',
                                }
                            ] }
                        >
                            <InputNumber />
                        </Form.Item>

                        { /* Description */ }
                        <Form.Item
                            name="description"
                            label="Description"
                            maxLength= { 200 }
                            rules={ [
                                {
                                    max: 200,
                                    type: 'string',
                                    message: 'Descriptions must be 200 characters or shorter.',
                                }
                            ] }
                        >
                            <Input.TextArea
                                rows={ 4 }
                            />
                        </Form.Item>

                        { /* Segments */ }
                        <Form.Item
                            name="segments"
                            valuePropName="segmentIds" 
                            layout="horizontal"
                            rules={ [
                                {
                                    required: true,
                                    message: 'Certificates are required to have at least one group.',
                                }
                            ] }
                        >

                            <RosterGroups 
                                className="groups" 
                                auth={ authService } 
                                form={ formController } 
                                handleSelectCallback={ handleSetSegments }
                                personId={ authService.getClaims()?.[ 'person_id' ] }
                                companyId={ authService.getClaims()?.[ 'company_id' ] }
                                groupAttributes={ groupSegmantationPayload === true 
                                    ? [ { name: 'client-managed', value: true } ]
                                    : undefined
                                }
                                rosterEditButton={ rosterEditEnabled }
                            />
                        </Form.Item>
                        
                        { /* File */ }
                        <Form.Item
                            name="file"
                            label="Template"
                            rules={ [
                                {
                                    required: true,
                                    message: 'Certificates are required to have a template.',
                                }
                            ] }
                        >
                             <FileUpload 
                                accept=".pdf"
                                onSelectFileCallback={ handleSelectFile }
                            /> 
                           
                        </Form.Item>

                        { /* CertificateTemplateId (hidden) */ }
                        <Form.Item
                            name="certificateTemplateId"
                            nostyle
                            style={ { display: 'none' } }
                        />
                    </Form>
                </Modal>
            }
        </>        
    );
};

export default CreateOverride;
